import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import PdMark from '../../components/PdMark';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Building2,
  ClipboardList,
  GitMerge,
  ArrowLeft,
  Brain,
  Library,
  FileType2,
  Tags,
  LifeBuoy,
  Activity,
  AlertTriangle,
  Settings as SettingsIcon,
  Plug,
  ListChecks,
  Layers,
  Link2,
  ScanSearch,
  Mail,
  MessageSquare,
  Eye,
  UserPlus,
  Trophy,
  HelpCircle,
  Lightbulb,
  ShieldAlert,
  Server,
  Cpu,
  HardDrive,
} from 'lucide-react';
import SecurityAlertBanner from '../../components/admin/SecurityAlertBanner';

// Top-level entries. Children render as inset secondary items, expanded
// automatically when any of them (or the parent) is the active route.
const SECTIONS = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/security', label: 'Security', icon: ShieldAlert },
  {
    to: '/admin/organisations',
    label: 'Organisations',
    icon: Building2,
    branch: ['/admin/organisations', '/admin/candidates', '/admin/merge'],
    children: [
      { to: '/admin/candidates', label: 'Candidates', icon: ClipboardList },
      { to: '/admin/merge', label: 'Merge tool', icon: GitMerge },
    ],
  },
  {
    to: '/admin/analysis',
    label: 'Analysis',
    icon: Brain,
    branch: ['/admin/analysis'],
    children: [
      { to: '/admin/analysis', label: 'Overview', icon: Activity, end: true },
      { to: '/admin/analysis/process/segmentation', label: 'Segmentation', icon: Layers },
      { to: '/admin/analysis/process/record_matching', label: 'Record matching', icon: Link2 },
      { to: '/admin/analysis/process/text_extraction', label: 'Text extraction', icon: Tags },
      { to: '/admin/analysis/process/radar', label: 'Copy comparison', icon: ScanSearch },
      { to: '/admin/analysis/process/email_classification', label: 'Email classification', icon: Mail },
      { to: '/admin/analysis/process/email_response_generation', label: 'Email responses', icon: MessageSquare },
      { to: '/admin/analysis/process/vision_ocr', label: 'Vision OCR', icon: Eye },
      { to: '/admin/analysis/process/file_review', label: 'Closer read', icon: ListChecks },
      { to: '/admin/analysis/process/org_summary', label: 'Organisation summary', icon: ListChecks },
      { to: '/admin/analysis/process/follow_up_scope_cleanup', label: 'Follow-up wording', icon: ListChecks },
      { to: '/admin/analysis/process/feature_idea_match', label: 'Feature idea matching', icon: Lightbulb },
      { to: '/admin/analysis/flagged', label: 'Flagged runs (all)', icon: AlertTriangle },
    ],
  },
  {
    to: '/admin/libraries',
    label: 'Libraries',
    icon: Library,
    branch: ['/admin/libraries'],
    children: [
      { to: '/admin/libraries/document-types', label: 'Document types', icon: FileType2 },
      { to: '/admin/libraries/tags', label: 'Tags', icon: Tags },
      { to: '/admin/libraries/support-orgs', label: 'Support orgs', icon: LifeBuoy },
    ],
  },
  {
    to: '/admin/settings',
    label: 'Settings',
    icon: SettingsIcon,
    branch: ['/admin/settings'],
    children: [
      { to: '/admin/settings/providers', label: 'Providers', icon: Plug },
    ],
  },
  {
    to: '/admin/infrastructure',
    label: 'Infrastructure',
    icon: Server,
    branch: ['/admin/infrastructure'],
    children: [
      { to: '/admin/infrastructure/workers', label: 'Analysis workers', icon: Cpu },
      { to: '/admin/infrastructure/storage', label: 'Storage', icon: HardDrive },
    ],
  },
  {
    to: '/admin/sar-help',
    label: 'SAR help queue',
    icon: HelpCircle,
    branch: ['/admin/sar-help'],
  },
  { to: '/admin/feature-ideas', label: 'Feature ideas', icon: Lightbulb },
  { to: '/admin/league-table', label: 'League table', icon: Trophy },
  { to: '/admin/early-access', label: 'Early access', icon: UserPlus },
  { to: '/admin/activity-logs', label: 'Activity logs', icon: ListChecks },
];

const AdminLayout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  if (!user) return null;
  if (user.role !== 'admin') return null;

  const inBranch = (paths) => paths.some((p) => location.pathname.startsWith(p));

  return (
    <div className="min-h-screen bg-ground flex flex-col min-[900px]:flex-row">
      <aside
        className="w-full min-[900px]:w-[248px] shrink-0 flex flex-col px-[14px] pt-[22px] pb-[18px] min-[900px]:sticky min-[900px]:top-0 min-[900px]:h-screen min-[900px]:overflow-y-auto"
        data-testid="admin-sidebar"
        aria-label="Admin"
      >
        <div className="flex items-center gap-3 px-[6px] mb-5">
          <PdMark size={28} className="text-brand shrink-0" />
          <span className="font-heading text-[17px] font-bold tracking-[-0.02em] text-ink whitespace-nowrap">Precious Data</span>
          <span className="inline-flex items-center rounded-full bg-inset px-[9px] py-[2px] text-[12px] font-semibold text-ink-70">Admin</span>
        </div>

        <Link
          to="/dashboard"
          className="flex items-center gap-1.5 rounded-[10px] px-[13px] py-[8px] text-sm text-ink-55 hover:text-ink transition-colors duration-state mb-3"
          data-testid="admin-exit-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to app
        </Link>

        <nav className="flex flex-col gap-0.5" aria-label="Admin sections">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const expanded = !!s.children && (s.branch ? inBranch(s.branch) : false);
            const slug = s.to.split('/').pop() || 'dashboard';
            return (
              <div key={s.to}>
                <NavLink
                  to={s.to}
                  end={s.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-[10px] px-[13px] py-[11px] text-[15px] transition-colors duration-state ${
                      isActive || (s.branch && inBranch(s.branch) && location.pathname === s.to)
                        ? 'bg-surface font-semibold text-ink'
                        : 'text-ink-55 hover:text-ink hover:bg-surface/60'
                    }`
                  }
                  data-testid={`admin-nav-${slug}`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {s.label}
                </NavLink>
                {s.children && expanded && (
                  <div className="mt-0.5 mb-1 ml-[22px] flex flex-col gap-0.5">
                    {s.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        end={c.end}
                        className={({ isActive }) =>
                          `flex items-center gap-2 rounded-[10px] px-[13px] py-[8px] text-sm transition-colors duration-state ${
                            isActive
                              ? 'bg-surface font-semibold text-ink'
                              : 'text-ink-55 hover:text-ink hover:bg-surface/60'
                          }`
                        }
                        data-testid={`admin-nav-${c.to.split('/').pop()}`}
                      >
                        <c.icon className="w-3.5 h-3.5 shrink-0" />
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <p className="mt-auto pt-6 px-[13px] text-[13px] text-ink-55 leading-snug">
          Signed in as <span className="font-medium text-ink">{user.full_name}</span>
        </p>
      </aside>

      <main className="flex-1 min-w-0 p-4 min-[900px]:py-[18px] min-[900px]:pr-[18px] min-[900px]:pl-0">
        <div className="bg-surface rounded-xl p-5 sm:p-8 min-h-[calc(100vh-36px)]">
          <SecurityAlertBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
